// Painel Super Master > Integrações — token/URL admin da uazapi
// (centralizado, nunca por empresa) + dashboard real de comportamento do
// WhatsApp (status de conexão por empresa + volume/sucesso de envio).
// Atrás de platformAuthGuard no backend (platform-integrations.controller.ts)
// — só acessível autenticado como PlatformAdmin.
import { platformDelete, platformGet, platformPost, platformPut } from "@/lib/api/gpsdrive-platform-client";

export interface UazapiSettings {
  baseUrl: string;
  baseUrlFromDatabase: boolean;
  hasAdminToken: boolean;
  adminTokenFromDatabase: boolean;
}

export interface UpdateUazapiSettingsInput {
  baseUrl?: string;
  adminToken?: string;
}

// Aba "OpenAI" (pedido 2026-09-11) — chave de API do OpenAI ou Gemini usada
// pelo wizard de assinatura de contrato (contrato.assinar.$token.tsx) pra
// conferir se a foto mostra o rosto da pessoa. Configuração única pra toda
// a plataforma, nunca por empresa. Nunca devolve a chave (nem cifrada).
export type FaceVerificationProvider = "openai" | "gemini";

export interface FaceVerificationStatus {
  enabled: boolean;
  provider: FaceVerificationProvider | null;
  hasApiKey: boolean;
}

export interface UpdateFaceVerificationSettingsInput {
  provider: FaceVerificationProvider;
  // Omitido mantém a chave já salva (só troca provider/enabled).
  apiKey?: string;
  enabled: boolean;
}

export interface WhatsappIntegrationsDashboard {
  connection: { connected: number; connecting: number; disconnected: number; total: number };
  sends: {
    totals: { success: number; failed: number };
    daily: { date: string; success: number; failed: number }[];
    recentFailures: {
      id: string;
      companyId: string;
      companyName: string;
      kind: string;
      errorMessage: string | null;
      createdAt: string;
    }[];
  };
}

// WhatsApp da PLATAFORMA (pedido 2026-09-24): número do Super Master,
// separado das empresas, que manda os alertas de segurança do servidor
// (logins/tentativas SSH) para um grupo escolhido aqui.
export interface SecurityWhatsappStatus {
  available: boolean;
  connected: boolean;
  status: string;
  phone: string | null;
  groupJid: string | null;
  groupName: string | null;
}

export interface SecurityWhatsappGroup {
  jid: string;
  name: string;
}

// "WhatsApp das empresas" (pedido 2026-09-25): o Super Master vincula a
// uma empresa uma instância uazapi que já existe (usada também por outro
// sistema) colando o token dela — sem QR code, e o backend nunca mexe no
// webhook nem desloga essa instância.
export interface CompanyWhatsappRow {
  companyId: string;
  companyName: string;
  status: string;
  phone: string | null;
  external: boolean;
  hasInstance: boolean;
}

const SECURITY_WHATSAPP = "/api/v1/platform-integrations/security-whatsapp";

export const gpsDrivePlatformIntegrationsService = {
  getUazapiSettings: () => platformGet<UazapiSettings>("/api/v1/platform-integrations/uazapi"),
  updateUazapiSettings: (input: UpdateUazapiSettingsInput) =>
    platformPut<UazapiSettings>("/api/v1/platform-integrations/uazapi", input),
  getWhatsappDashboard: (days: number) =>
    platformGet<WhatsappIntegrationsDashboard>(`/api/v1/platform-integrations/whatsapp/dashboard?days=${days}`),

  getFaceVerificationSettings: () =>
    platformGet<FaceVerificationStatus>("/api/v1/platform-integrations/face-verification"),
  updateFaceVerificationSettings: (input: UpdateFaceVerificationSettingsInput) =>
    platformPut<FaceVerificationStatus>("/api/v1/platform-integrations/face-verification", input),

  getSecurityWhatsapp: () => platformGet<SecurityWhatsappStatus>(SECURITY_WHATSAPP),
  connectSecurityWhatsapp: () =>
    platformPost<{ qrcode: string | null; paircode: string | null; connected: boolean }>(`${SECURITY_WHATSAPP}/connect`),
  disconnectSecurityWhatsapp: () => platformPost<{ ok: true }>(`${SECURITY_WHATSAPP}/disconnect`),
  listSecurityWhatsappGroups: (force: boolean) =>
    platformGet<{ groups: SecurityWhatsappGroup[] }>(`${SECURITY_WHATSAPP}/groups?force=${force}`),
  setSecurityWhatsappGroup: (group: SecurityWhatsappGroup | null) =>
    platformPut<SecurityWhatsappStatus>(`${SECURITY_WHATSAPP}/group`, { group }),
  listCompanyWhatsapp: () => platformGet<CompanyWhatsappRow[]>("/api/v1/platform-integrations/whatsapp/companies"),
  attachCompanyExternalInstance: (companyId: string, token: string) =>
    platformPut<unknown>(`/api/v1/platform-integrations/whatsapp/companies/${companyId}/external-instance`, { token }),
  detachCompanyExternalInstance: (companyId: string) =>
    platformDelete(`/api/v1/platform-integrations/whatsapp/companies/${companyId}/external-instance`),

  testSecurityWhatsapp: () => platformPost<{ ok: true }>(`${SECURITY_WHATSAPP}/test`),
};
